import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { hasEditPermission } from '../../utils/authConfig';
import {
  apiErrorMessage,
  createClientFeature,
  fetchClientVersionRules,
  fetchSeenVersions,
  updateClientFeature,
  updatePlatformVersions,
} from '../../services/clientVersionApi';

// OC-725: which SongSnap app versions get which features, update thresholds per platform,
// and the app versions seen calling the API. An app that sends no version never gets a feature.
const EMPTY_FEATURE = { key: '', name: '', description: '', enabled: false, min_version_ios: '', min_version_android: '' };
const SOURCE_LABELS = { header: 'header', body: 'body', none: 'no version' };

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');
const blank = (value) => (value === null || value === undefined ? '' : value);

function featureForm(feature) {
  return {
    name: feature.name || '',
    description: blank(feature.description),
    enabled: !!feature.enabled,
    min_version_ios: blank(feature.min_version_ios),
    min_version_android: blank(feature.min_version_android),
  };
}

function toPayload(form) {
  return {
    ...form,
    min_version_ios: form.min_version_ios.trim() || null,
    min_version_android: form.min_version_android.trim() || null,
  };
}

function AppVersionSettings() {
  const canEdit = hasEditPermission();
  const [features, setFeatures] = useState([]);
  const [forms, setForms] = useState({});
  const [platforms, setPlatforms] = useState({});
  const [newFeature, setNewFeature] = useState(EMPTY_FEATURE);
  const [seen, setSeen] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [seenLoading, setSeenLoading] = useState(false);
  const [saving, setSaving] = useState('');
  const [message, setMessage] = useState(null);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchClientVersionRules();
      const rules = data.data || {};
      setFeatures(rules.features || []);
      setForms(Object.fromEntries((rules.features || []).map((f) => [f.id, featureForm(f)])));
      setPlatforms(Object.fromEntries((rules.platforms || []).map((p) => [p.platform, {
        min_supported_version: blank(p.min_supported_version),
        recommended_version: blank(p.recommended_version),
      }])));
    } catch (error) {
      setMessage({ variant: 'danger', text: apiErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSeen = useCallback(async () => {
    setSeenLoading(true);
    try {
      const { data } = await fetchSeenVersions(days);
      setSeen(data.data?.items || []);
    } catch (error) {
      setMessage({ variant: 'danger', text: apiErrorMessage(error) });
    } finally {
      setSeenLoading(false);
    }
  }, [days]);

  useEffect(() => { loadRules(); }, [loadRules]);
  useEffect(() => { loadSeen(); }, [loadSeen]);

  const setFormField = (id, field, value) => {
    setForms((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveFeature = async (id) => {
    setSaving(`feature-${id}`);
    try {
      await updateClientFeature(id, toPayload(forms[id]));
      setMessage({ variant: 'success', text: 'Feature saved. Apps pick it up within a minute.' });
      await loadRules();
    } catch (error) {
      setMessage({ variant: 'danger', text: apiErrorMessage(error) });
    } finally {
      setSaving('');
    }
  };

  const addFeature = async (event) => {
    event.preventDefault();
    setSaving('new');
    try {
      await createClientFeature({ ...toPayload(newFeature), key: newFeature.key.trim() });
      setNewFeature(EMPTY_FEATURE);
      setMessage({ variant: 'success', text: 'Feature added.' });
      await loadRules();
    } catch (error) {
      setMessage({ variant: 'danger', text: apiErrorMessage(error) });
    } finally {
      setSaving('');
    }
  };

  const savePlatform = async (platform) => {
    setSaving(`platform-${platform}`);
    const values = platforms[platform];
    try {
      await updatePlatformVersions(platform, {
        min_supported_version: values.min_supported_version.trim() || null,
        recommended_version: values.recommended_version.trim() || null,
      });
      setMessage({ variant: 'success', text: `${platform} versions saved.` });
      await loadRules();
    } catch (error) {
      setMessage({ variant: 'danger', text: apiErrorMessage(error) });
    } finally {
      setSaving('');
    }
  };

  const versionInput = (value, onChange, label) => (
    <Form.Control
      size="sm"
      value={value}
      placeholder="off"
      aria-label={label}
      disabled={!canEdit}
      onChange={(e) => onChange(e.target.value)}
      style={{ maxWidth: 110 }}
    />
  );

  return (
    <div className="container-fluid app-version-settings">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">App Versions</h2>
        <Button variant="outline-secondary" size="sm" onClick={() => { loadRules(); loadSeen(); }}>
          <FontAwesomeIcon icon={faSyncAlt} className="me-1" /> Refresh
        </Button>
      </div>

      {message && (
        <Alert variant={message.variant} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>
      )}

      <Card className="mb-4">
        <Card.Header><Card.Title className="mb-0">Features by app version</Card.Title></Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            A feature is on for an app when it is enabled and the app's version is at least the minimum for its
            platform. Leave a minimum empty to keep the feature off on that platform. Apps that send no version
            never get a feature. Versions look like <code>1.31</code> or <code>1.31.0</code>.
          </p>
          {loading ? <Spinner animation="border" size="sm" /> : (
            <Table responsive hover size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Enabled</th>
                  <th>Min iOS</th>
                  <th>Min Android</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {features.map((feature) => {
                  const form = forms[feature.id] || featureForm(feature);
                  return (
                    <tr key={feature.id}>
                      <td>
                        <div className="fw-semibold">{feature.name}</div>
                        <code className="small">{feature.key}</code>
                        {feature.description && <div className="text-muted small">{feature.description}</div>}
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          id={`feature-enabled-${feature.id}`}
                          checked={form.enabled}
                          disabled={!canEdit}
                          aria-label={`Enable ${feature.name}`}
                          onChange={(e) => setFormField(feature.id, 'enabled', e.target.checked)}
                        />
                      </td>
                      <td>{versionInput(form.min_version_ios, (v) => setFormField(feature.id, 'min_version_ios', v), `Minimum iOS version for ${feature.name}`)}</td>
                      <td>{versionInput(form.min_version_android, (v) => setFormField(feature.id, 'min_version_android', v), `Minimum Android version for ${feature.name}`)}</td>
                      <td className="small text-muted">{formatDate(feature.updated_at)}</td>
                      <td className="text-end">
                        {canEdit && (
                          <Button size="sm" variant="primary" disabled={saving === `feature-${feature.id}`} onClick={() => saveFeature(feature.id)}>
                            <FontAwesomeIcon icon={faSave} className="me-1" /> Save
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!features.length && (
                  <tr><td colSpan={6} className="text-muted text-center">No features yet.</td></tr>
                )}
              </tbody>
            </Table>
          )}

          {canEdit && (
            <Form onSubmit={addFeature} className="row g-2 align-items-end mt-2">
              <Form.Group className="col-md-2">
                <Form.Label className="small">Key</Form.Label>
                <Form.Control size="sm" required placeholder="new_feature_key" value={newFeature.key}
                  onChange={(e) => setNewFeature({ ...newFeature, key: e.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label className="small">Name</Form.Label>
                <Form.Control size="sm" required value={newFeature.name}
                  onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-3">
                <Form.Label className="small">Description</Form.Label>
                <Form.Control size="sm" value={newFeature.description}
                  onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })} />
              </Form.Group>
              <Form.Group className="col-md-2">
                <Form.Label className="small">Min iOS / Android</Form.Label>
                <div className="d-flex gap-1">
                  <Form.Control size="sm" placeholder="iOS" aria-label="New feature minimum iOS version" value={newFeature.min_version_ios}
                    onChange={(e) => setNewFeature({ ...newFeature, min_version_ios: e.target.value })} />
                  <Form.Control size="sm" placeholder="Android" aria-label="New feature minimum Android version" value={newFeature.min_version_android}
                    onChange={(e) => setNewFeature({ ...newFeature, min_version_android: e.target.value })} />
                </div>
              </Form.Group>
              <div className="col-md-2">
                <Button type="submit" size="sm" variant="outline-primary" disabled={saving === 'new'}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" /> Add feature
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header><Card.Title className="mb-0">Update thresholds</Card.Title></Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            Apps below the minimum supported version are told to update (<code>update_required</code>); apps below
            the recommended version are offered an update. Apps that send no version are never asked.
          </p>
          <Table responsive size="sm" className="align-middle mb-0">
            <thead>
              <tr><th>Platform</th><th>Minimum supported</th><th>Recommended</th><th /></tr>
            </thead>
            <tbody>
              {['ios', 'android'].map((platform) => {
                const values = platforms[platform] || { min_supported_version: '', recommended_version: '' };
                const setValue = (field, value) => setPlatforms((prev) => ({ ...prev, [platform]: { ...values, [field]: value } }));
                return (
                  <tr key={platform}>
                    <td>{platform === 'ios' ? 'iOS' : 'Android'}</td>
                    <td>{versionInput(values.min_supported_version, (v) => setValue('min_supported_version', v), `${platform} minimum supported version`)}</td>
                    <td>{versionInput(values.recommended_version, (v) => setValue('recommended_version', v), `${platform} recommended version`)}</td>
                    <td className="text-end">
                      {canEdit && (
                        <Button size="sm" variant="primary" disabled={saving === `platform-${platform}`} onClick={() => savePlatform(platform)}>
                          <FontAwesomeIcon icon={faSave} className="me-1" /> Save
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <Card.Title className="mb-0">App versions calling the API</Card.Title>
          <Form.Select size="sm" style={{ width: 140 }} value={days} aria-label="Period"
            onChange={(e) => setDays(Number(e.target.value))}>
            <option value={1}>Today</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </Form.Select>
        </Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            Counted per day from <code>X-App-Version</code> / <code>X-Platform</code>, or <code>app_version</code> in
            the request when the header is missing. Admin and test requests are not counted.
          </p>
          {seenLoading ? <Spinner animation="border" size="sm" /> : (
            <Table responsive hover size="sm" className="align-middle mb-0">
              <thead>
                <tr><th>Version</th><th>Platform</th><th className="text-end">Requests</th><th className="text-end">Devices</th><th>Source</th><th>Last seen</th></tr>
              </thead>
              <tbody>
                {seen.map((row) => (
                  <tr key={`${row.platform}-${row.app_version}`}>
                    <td className="fw-semibold">{row.app_version === 'unknown' ? <span className="text-muted">no version</span> : row.app_version}</td>
                    <td>{row.platform === 'unknown' ? <span className="text-muted">unknown</span> : row.platform}</td>
                    <td className="text-end">{row.requests.toLocaleString()}</td>
                    <td className="text-end">{row.devices.toLocaleString()}</td>
                    <td>
                      {Object.entries(row.sources || {}).map(([source, count]) => (
                        <Badge key={source} bg={source === 'header' ? 'success' : 'secondary'} className="text-white me-1">
                          {SOURCE_LABELS[source] || source}: {count.toLocaleString()}
                        </Badge>
                      ))}
                    </td>
                    <td className="small">{formatDate(row.last_seen_at)}</td>
                  </tr>
                ))}
                {!seen.length && (
                  <tr><td colSpan={6} className="text-muted text-center">No app requests recorded in this period.</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default AppVersionSettings;
